import React from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  ListItemText
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import SubscriptionPlan from '../../types/Subscription';
import { loadStripe } from '@stripe/stripe-js';
import subscriptionService from '../../services/SubscriptionService';

// Initialize Stripe with the publishable key from environment variables
// The key must start with 'pk_test_' or 'pk_live_'
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? 
  loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) : 
  null; // Will be handled in the component

interface SubscriptionProductCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  isSelected?: boolean;
  onSelectPlan?: (planId: string) => void;
  isProcessing?: boolean;
  isAnnualPlan?: boolean;
}

const SubscriptionProductCard: React.FC<SubscriptionProductCardProps> = ({
  plan,
  isCurrentPlan = false,
  isSelected = false,
  onSelectPlan,
  isProcessing = false,
  isAnnualPlan = false
}) => {
  const handleSelectPlan = async () => {
    if (!isProcessing && !isCurrentPlan) {
      // If the parent provided a handler, use that (for custom checkout flows)
      if (onSelectPlan) {
        onSelectPlan(plan.subscription_stripe_id);
      } else {
        // Direct Stripe checkout implementation
        try {
          // Check if Stripe is properly initialized
          if (!stripePromise) {
            console.error('Stripe is not initialized. Make sure REACT_APP_STRIPE_PUBLISHABLE_KEY is set in your .env file');
            alert('Payment system is not properly configured. Please contact support.');
            return;
          }
          // Get organization ID from local storage or context
          const organizationId = localStorage.getItem('organizationId') || '';
          if (!organizationId) {
            console.error('No organization ID found');
            return;
          }
          
          // Generate success and cancel URLs
          const baseUrl = window.location.origin;
          const successUrl = `${baseUrl}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
          const cancelUrl = `${baseUrl}/subscription/cancel?canceled=true`;
          
          // Use the subscription service instead of direct fetch
          const response = await subscriptionService.createCheckoutSession(
            organizationId,
            plan.subscription_stripe_id,
            successUrl,
            cancelUrl,
            isAnnualPlan // Pass the isAnnualPlan prop as is_subscription parameter
          );
          
          // Redirect to Stripe Checkout
          if (response.url) {
            window.location.href = response.url;
          }
        } catch (error) {
          console.error('Error creating checkout session:', error);
        }
      }
    }
  };

  // Calculate if the button should be disabled
  const isButtonDisabled = isProcessing || isCurrentPlan;

  return (
    <Card
      elevation={isSelected ? 3 : 1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'white',
        border: isSelected ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          transform: 'translateY(-4px)'
        }
      }}
    >
      {/* Current Plan Banner */}
      {isCurrentPlan && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: 'success.main', // Changed from primary.main to success.main (green)
            color: 'white',
            padding: '4px 12px',
            borderBottomLeftRadius: 8,
            fontSize: '0.75rem',
            fontWeight: 'bold',
          }}
        >
          Current Plan
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        {/* Plan Name */}
        <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight="bold">
          {plan.subscription_name}
        </Typography>

        {/* Price */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h4" component="p" color="primary" fontWeight="bold">
            {plan.subscription_price === 0 ? 'Free' : plan.subscription_price}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {plan.subscription_price === 0 ? 'community plan' : 'per year'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Description - Displayed with more emphasis between dividers */}
        <Typography variant="body1" color="text.primary" paragraph sx={{ my: 2 }}>
          {plan.description || plan.subscription_description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Features - Only rendered if there are features */}
        {plan.key_features && plan.key_features.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Key Features:
            </Typography>

            <List dense disablePadding>
              {plan.key_features.map((feature, index) => (
                <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          variant={isCurrentPlan ? "outlined" : "contained"}
          color="primary"
          fullWidth
          onClick={handleSelectPlan}
          disabled={isButtonDisabled}
        >
          {isProcessing ? 'Processing...' :
            isCurrentPlan ? 'Current Plan' : 'Select Plan'}
        </Button>
      </CardActions>
    </Card>
  );
};

export default SubscriptionProductCard;
